import { useState } from 'react';
import type { NormalizedCapacitySlot } from './capacityUtils';
import type { TimeSlot } from './types';
import type { StationarySlotHold } from './useReserveStationarySlot';
import { useReserveStationarySlot } from './useReserveStationarySlot';
import { isStationaryHoldExpired, useCancelHoldOnExpiry } from './useStationaryHoldLifecycle';

type Args = {
  garageId?: number;
  salesItemIds?: number[];
  reservation: StationarySlotHold | null;
  onReservationChange: (reservation: StationarySlotHold | null) => void;
  slotLookup: Map<string, NormalizedCapacitySlot>;
  resolveSelectedSlotId: (selected: { startsAt: string; endsAt: string } | null) => string | null;
  onReserveFailed: (error: unknown) => void;
};

/**
 * Shared hold-on-select + TTL expiry for customer + partner stationary pickers.
 * Same tile while live is a no-op; expired hold re-reserves the window.
 */
export function useStationaryHoldOnSelect({
  garageId,
  salesItemIds,
  reservation,
  onReservationChange,
  slotLookup,
  resolveSelectedSlotId,
  onReserveFailed
}: Args) {
  const { reserveSlot, cancelHold, isPending: isReservationPending } = useReserveStationarySlot();
  const isReservationExpired = useCancelHoldOnExpiry(reservation);
  // Which tile is being held, so the picker can show the spinner on that tile
  // instead of replacing the whole grid with a skeleton.
  const [pendingSlotId, setPendingSlotId] = useState<string | null>(null);

  // Keep credentials after client-side expiry so cancel-before-re-reserve can free the bay.
  const holdCredentials = reservation
    ? {
        slotId: reservation.slotId,
        ticket: reservation.ticket,
        secret: reservation.secret
      }
    : null;

  const holdAndSelect = async (slot: NormalizedCapacitySlot) => {
    if (garageId == null || slot.workstationId == null) {
      return;
    }

    setPendingSlotId(slot.id);
    try {
      const held = await reserveSlot({
        start: slot.start,
        end: slot.end,
        garageId,
        salesItemIds: salesItemIds?.length ? salesItemIds : undefined,
        workstationId: slot.workstationId,
        previousHold: holdCredentials,
        // The replace fallback already cancelled it; keeping it would let checkout
        // submit a dead reservation (409 at booking create).
        onPreviousHoldLost: () => onReservationChange(null)
      });
      onReservationChange(held);
    } catch (error) {
      // Reserve-first leaves the previous hold live on failure unless the fallback
      // released it (cleared above) — user can retry either way.
      // The grid is left exactly as it was: pulling the tile the customer just
      // tapped out from under them reads as the picker breaking, not as a lost race.
      onReserveFailed(error);
    } finally {
      setPendingSlotId(null);
    }
  };

  const handleSelectSlot = async (timeSlot: TimeSlot) => {
    const slot = slotLookup.get(timeSlot.id);
    if (!slot) {
      return;
    }

    const currentId = resolveSelectedSlotId(
      reservation ? { startsAt: reservation.startsAt, endsAt: reservation.endsAt } : null
    );
    if (currentId === slot.id && !isStationaryHoldExpired(reservation)) {
      return;
    }

    await holdAndSelect(slot);
  };

  /** Clear store hold only — stale-hold subscribers own the bay cancel. */
  const clearHold = () => {
    onReservationChange(null);
  };

  return {
    handleSelectSlot,
    clearHold,
    cancelHold,
    pendingSlotId,
    isReservationPending,
    isReservationExpired,
    selectedSlotId: resolveSelectedSlotId(
      reservation ? { startsAt: reservation.startsAt, endsAt: reservation.endsAt } : null
    )
  };
}
